import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { CampaignDetail, Client, PlatformId } from '../../shared/types';
import { api, patch, post } from '../lib/api';
import { useConfig } from '../lib/session';
import { errorMessage, useToast } from './toast';
import { Dialog, Field, Notice } from './ui';

export function CampaignDialog({
  open,
  onClose,
  campaign,
  defaultClientId,
}: {
  open: boolean;
  onClose: () => void;
  campaign?: CampaignDetail;
  defaultClientId?: string;
}) {
  const { data: config } = useConfig();
  const clients = useQuery({
    queryKey: ['clients'],
    queryFn: () => api<Client[]>('/api/clients'),
    enabled: open,
  });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const [clientId, setClientId] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [platforms, setPlatforms] = useState<PlatformId[]>([]);
  const [visible, setVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setClientId(campaign?.client.id ?? defaultClientId ?? '');
    setName(campaign?.name ?? '');
    setDescription(campaign?.description ?? '');
    setDueDate(campaign?.dueDate ?? '');
    setPlatforms(campaign?.platforms ?? config?.defaultPlatforms ?? ['google_ads', 'cm360']);
    setVisible(campaign?.visibleToClient ?? false);
    setError(null);
  }, [open, campaign, defaultClientId, config?.defaultPlatforms]);

  useEffect(() => {
    if (open && !clientId && clients.data?.length) setClientId(clients.data[0].id);
  }, [open, clientId, clients.data]);

  const save = async () => {
    setBusy(true);
    setError(null);
    const body = {
      clientId,
      name,
      description,
      dueDate: dueDate || null,
      platforms,
      visibleToClient: visible,
    };
    try {
      if (campaign) {
        await patch(`/api/campaigns/${campaign.id}`, body);
        toast('Kampagne gespeichert.');
      } else {
        const { id } = await post<{ id: string }>('/api/campaigns', body);
        navigate(`/kampagnen/${id}`);
      }
      await queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      if (campaign) await queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id] });
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const noClients = clients.data && clients.data.length === 0;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={campaign ? 'Kampagne bearbeiten' : 'Neue Kampagne'}
      wide
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn btn-primary" disabled={busy || !name.trim() || !clientId} onClick={save}>
            {campaign ? 'Speichern' : 'Kampagne anlegen'}
          </button>
        </>
      }
    >
      {error && <Notice kind="error">{error}</Notice>}
      {noClients && <Notice kind="warn">Lege zuerst unter „Kunden“ einen Kunden an.</Notice>}
      <div className="form-grid two">
        <Field label="Kunde">
          {(id) => (
            <select id={id} className="select" value={clientId} onChange={(e) => setClientId(e.target.value)}>
              {clients.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Freigabe bis (optional)">
          {(id) => (
            <input
              id={id}
              className="input"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          )}
        </Field>
      </div>
      <Field label="Name der Kampagne">
        {(id) => (
          <input
            id={id}
            className="input"
            value={name}
            maxLength={160}
            placeholder="z. B. Herbstkampagne 2026"
            onChange={(e) => setName(e.target.value)}
          />
        )}
      </Field>
      <Field label="Beschreibung / Hinweis für den Kunden" hint="Erscheint oben in der Kundenansicht.">
        {(id) => (
          <textarea
            id={id}
            className="textarea"
            value={description}
            maxLength={4000}
            onChange={(e) => setDescription(e.target.value)}
          />
        )}
      </Field>
      <div className="field">
        <span className="field-label">Zielplattformen für den Technik-Check</span>
        <div className="form-grid two" style={{ gap: 8 }}>
          {config?.platforms.map((p) => (
            <label key={p.id} className="check">
              <input
                type="checkbox"
                checked={platforms.includes(p.id)}
                onChange={(e) =>
                  setPlatforms((list) =>
                    e.target.checked ? [...list, p.id] : list.filter((x) => x !== p.id),
                  )
                }
              />
              {p.name}
            </label>
          ))}
        </div>
      </div>
      <label className="check">
        <input type="checkbox" checked={visible} onChange={(e) => setVisible(e.target.checked)} />
        <span>
          <strong>Für Kundenkonten sichtbar</strong>
          <span className="field-hint" style={{ display: 'block' }}>
            Eingeladene Personen des Kunden sehen die Kampagne nach dem Login. Freigabe-Links funktionieren
            unabhängig davon.
          </span>
        </span>
      </label>
    </Dialog>
  );
}
